import meetingAvailableIcon from 'assets/images/calendar/attendee-accepted.svg'
import meetingNotAvailableIcon from 'assets/images/calendar/attendee-declined.svg'
import meetingUnkownStatusIcon from 'assets/images/calendar/attendee-not-responding.svg'
import { TimeRange } from './SchedulingUtils'
import { BusyTimeData, hasConflictWithMultipleRanges } from './conflictDetection'

// ============================================================================
// AttendeeStatusFlags, AttendeeStatusIcon type.
// ============================================================================

export interface AttendeeStatusFlags {
  unknown?: boolean
  noData?: boolean
  hasBusyTimes?: boolean
  hasConflict?: boolean
}

export interface AttendeeStatusIcon {
  statusIcon: string | null
  statusAlt: string
}

// ============================================================================
// 참석자 상태 관련 유틸리티 함수
// ============================================================================

/**
 * Determine status icon for an attendee based on availability
 *
 * @param attendee - Attendee with status flags
 * @param selectedTimeRange - Single selected time range { start: Date, end: Date } or null
 * @param selectedTimeRanges - Array of selected time ranges for multi-mode or null
 * @param busyData - Attendee's busy time data (required for multi-mode conflict detection)
 * @returns Object containing { statusIcon: string|null, statusAlt: string }
 *
 * Status icon logic:
 * - No time range selected: No icon shown
 * - Query failed (unknown): Gray question mark icon
 * - No busy times (noData or !hasBusyTimes): Green check icon (FREE/Available)
 * - Has conflict: Red X icon (Not Available)
 * - Has busy times but no conflict: Green check icon (Available)
 */
export const getAttendeeStatusIcon = (
  attendee: AttendeeStatusFlags,
  selectedTimeRange: TimeRange | null | undefined,
  selectedTimeRanges?: TimeRange[] | null | undefined,
  busyData?: BusyTimeData | null | undefined,
): AttendeeStatusIcon => {
  // Multi-mode: check if we have multiple selections
  const isMultiMode = selectedTimeRanges && selectedTimeRanges.length > 0

  // No selection - no status icon
  if (!selectedTimeRange && !isMultiMode) {
    return { statusIcon: null, statusAlt: '' }
  }

  if (attendee.unknown) {
    // Query failed - unknown status (gray question mark)
    return { statusIcon: meetingUnkownStatusIcon, statusAlt: 'Unknown' }
  } else if (attendee.noData || !attendee.hasBusyTimes) {
    // No busy times - FREE status (green check)
    return { statusIcon: meetingAvailableIcon, statusAlt: 'Available' }
  } else if (isMultiMode && busyData) {
    // Multi-mode: check conflict with ANY of the selections
    const hasAnyConflict = hasConflictWithMultipleRanges(busyData, selectedTimeRanges)
    if (hasAnyConflict) {
      return { statusIcon: meetingNotAvailableIcon, statusAlt: 'Not Available' }
    } else {
      return { statusIcon: meetingAvailableIcon, statusAlt: 'Available' }
    }
  } else if (attendee.hasConflict) {
    // Single-mode: has busy times and conflicts with selection - not available (red X)
    return { statusIcon: meetingNotAvailableIcon, statusAlt: 'Not Available' }
  } else {
    // Has busy times but no conflict - available (green check)
    return { statusIcon: meetingAvailableIcon, statusAlt: 'Available' }
  }
}
